import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setProfitPercent } from 'src/redux/slices/settings-slice';
import { axiosInstanceNew } from 'src/utils/axios';
import { fromBigIntWithDecimals } from 'src/utils/crypto/formatAmount';
import { isIncomingTxFresh } from 'src/constants/transactionStatus';

const DEFAULT_PAGE_SIZE = 10;

const EMPTY_CONTRACT_BALANCE = {
  loading: false,
  error: null,
  paused: false,
  owner: '',
  contractAddress: '',
  eth: '',
  usdt: '',
  raw: {
    eth: '0',
    usdt: '0',
  },
  updatedAt: '',
};

function ensureContractBalanceState(state) {
  if (!state.contractBalance) {
    state.contractBalance = { ...EMPTY_CONTRACT_BALANCE };
  }

  if (!state.contractBalance.raw) {
    state.contractBalance.raw = { eth: '0', usdt: '0' };
  }

  return state.contractBalance;
}

function formatContractBalance(value, decimals) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const formatted = fromBigIntWithDecimals(value, decimals);

  if (formatted === null || formatted === undefined || formatted === '') {
    return '0';
  }

  return formatted;
}

export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchDashboardStats',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosInstanceNew.get('/admin/dashboard');

      dispatch(
        setProfitPercent({
          profitPercent: res?.data?.settings?.profitPercent,
          totalVolume: res?.data?.kpis?.volumeInUsd,
        })
      );

      return res.data;
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to fetch dashboard stats';
      return rejectWithValue(msg);
    }
  }
);

export const fetchDashboardTxs = createAsyncThunk(
  'dashboard/fetchTxs',
  async (admin, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post('/transactions/all', { admin });
      return res.data.transactions;
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to fetch dashboard txs';
      return rejectWithValue(msg);
    }
  }
);

export const fetchDashboardContractBalance = createAsyncThunk(
  'dashboard/fetchContractBalance',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstanceNew.get('/admin/contract/status');

      const contract = data?.contract || {};
      const balances = contract?.balances || {};

      return {
        paused: Boolean(contract.paused),
        owner: contract.owner || '',
        contractAddress: contract.contractAddress || '',
        eth: formatContractBalance(balances.eth, 18),
        usdt: formatContractBalance(balances.usdt, 6),
        raw: {
          eth: balances.eth || '0',
          usdt: balances.usdt || '0',
        },
        updatedAt: new Date().toISOString(),
      };
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to fetch contract balance';

      return rejectWithValue(msg);
    }
  }
);

function findTxIndex(items, txId) {
  if (!Array.isArray(items) || !txId) return -1;

  const id = String(txId);
  return items.findIndex((item) => String(item?._id) === id);
}

function replaceVisibleTx(state, updatedTx) {
  if (!updatedTx?._id) return false;

  if (!Array.isArray(state.dashboardTxs)) {
    state.dashboardTxs = [];
  }

  const idx = findTxIndex(state.dashboardTxs, updatedTx._id);
  if (idx === -1) return false;
  if (!isIncomingTxFresh(state.dashboardTxs[idx], updatedTx)) return false;

  state.dashboardTxs[idx] = {
    ...state.dashboardTxs[idx],
    ...updatedTx,
  };

  return true;
}

function prependNewTx(state, tx) {
  if (!tx?._id) return false;

  if (!Array.isArray(state.dashboardTxs)) {
    state.dashboardTxs = [];
  }

  const existingIdx = findTxIndex(state.dashboardTxs, tx._id);

  if (existingIdx !== -1) {
    const mergedTx = {
      ...state.dashboardTxs[existingIdx],
      ...tx,
    };

    state.dashboardTxs.splice(existingIdx, 1);
    state.dashboardTxs.unshift(mergedTx);

    return true;
  }

  state.dashboardTxs.unshift(tx);

  if (state.dashboardTxs.length > DEFAULT_PAGE_SIZE) {
    state.dashboardTxs.length = DEFAULT_PAGE_SIZE;
  }

  return true;
}

const initialState = {
  loading: false,
  error: null,

  settings: {
    profitPercent: 0,
    maxProfit: 0,
  },

  kpis: {
    txCount: 0,
    uniqueUsers: 0,
    volumeInUsd: 0,
    totalFeesEarnedUsd: 0,
    networkFeeUsd: 0,
    netUsd: 0,
    txCountWithFee: 0,
    totalFeesUsdAll: 0,
    totalUsers: 0,
    fiatBalance: 0,
  },

  trends: {
    last8Days: [],
  },

  dashboardTxs: [],

  contractBalance: EMPTY_CONTRACT_BALANCE,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,

  reducers: {
    updateBalance(state, action) {
      const { fiatBalance } = action.payload || {};
      state.kpis.fiatBalance = fiatBalance;
    },

    mergeDashboardTransaction(state, action) {
      replaceVisibleTx(state, action.payload);
    },

    prependDashboardTransaction(state, action) {
      prependNewTx(state, action.payload);
    },

    clearDashboardContractBalanceError(state) {
      const contractBalance = ensureContractBalanceState(state);
      contractBalance.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        state.settings = action.payload?.settings || initialState.settings;
        state.kpis = action.payload?.kpis || initialState.kpis;
        state.trends = action.payload?.trends || initialState.trends;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch dashboard stats';
      })

      .addCase(fetchDashboardTxs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardTxs.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (Array.isArray(action.payload)) {
          state.dashboardTxs = action.payload;
        } else {
          state.dashboardTxs = [];
        }
      })
      .addCase(fetchDashboardTxs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch dashboard txs';
      })

      .addCase(fetchDashboardContractBalance.pending, (state) => {
        const contractBalance = ensureContractBalanceState(state);

        contractBalance.loading = true;
        contractBalance.error = null;
      })
      .addCase(fetchDashboardContractBalance.fulfilled, (state, action) => {
        const contractBalance = ensureContractBalanceState(state);

        contractBalance.loading = false;
        contractBalance.error = null;
        contractBalance.paused = Boolean(action.payload?.paused);
        contractBalance.owner = action.payload?.owner || '';
        contractBalance.contractAddress = action.payload?.contractAddress || '';
        contractBalance.eth = action.payload?.eth || '0';
        contractBalance.usdt = action.payload?.usdt || '0';
        contractBalance.raw = action.payload?.raw || { eth: '0', usdt: '0' };
        contractBalance.updatedAt = action.payload?.updatedAt || '';
      })
      .addCase(fetchDashboardContractBalance.rejected, (state, action) => {
        const contractBalance = ensureContractBalanceState(state);

        contractBalance.loading = false;
        contractBalance.error =
          action.payload || action.error?.message || 'Failed to fetch contract balance';
      });
  },
});

export const {
  updateBalance,
  prependDashboardTransaction,
  mergeDashboardTransaction,
  clearDashboardContractBalanceError,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
